import styled, { css } from 'styled-components';
import { createReadableClass } from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { getPlayerControls } from '../../../helpers';
import { useSource } from '../../sources';
import {
  Loop,
  Next,
  PlaylistButton,
  PlayPause,
  Prev,
  Shuffle,
  VolumeComponent
} from './buttons';

interface SmallPlayControlsProps {
  forceOneLine?: boolean;
}

export function SmallPlayControls({
  forceOneLine = false
}: SmallPlayControlsProps) {
  const { items } = useSource();
  const {
    sourceType,
    controls: derivedControls,
    existingPlayerControls,
    playerControls
  } = useSettings();

  const controls = getPlayerControls(
    derivedControls,
    existingPlayerControls,
    playerControls
  );

  const isPlayer = sourceType === 'player';

  const withMultipleTracks = items?.length > 1;
  const withTrackControls = controls.includes('trackControls');
  const withChangeTrackControls =
    sourceType === 'rss' || (withMultipleTracks && withTrackControls);

  const withShuffle = controls.includes('shuffle') && isPlayer;
  const withLoop = controls.includes('loop') && isPlayer;
  const withVolume = controls.includes('volume');
  const withPlaylist = controls.includes('playlist');
  const secondaryControlsCount = [
    withShuffle,
    withLoop,
    withVolume,
    withPlaylist
  ].filter(Boolean).length;
  const showControlsInOneLine =
    forceOneLine || !withMultipleTracks || secondaryControlsCount <= 2;

  if (!withTrackControls && !secondaryControlsCount) {
    return null;
  }

  return (
    <Container _showInOneLine={showControlsInOneLine}>
      {withTrackControls && (
        <MainControls>
          {withChangeTrackControls && <Prev />}
          <PlayPause />
          {withChangeTrackControls && <Next />}
        </MainControls>
      )}

      {!!secondaryControlsCount && (
        <SecondaryControls
          _withMainControls={withTrackControls}
          _showInOneLine={showControlsInOneLine}
        >
          {withShuffle && <Shuffle withoutMargin />}
          {withLoop && <Loop withoutMargin />}

          {withVolume && <VolumeComponent withoutMargin />}
          {withPlaylist && <PlaylistButton withoutMargin />}
        </SecondaryControls>
      )}
    </Container>
  );
}

const Container = styled.div.attrs<{
  _showInOneLine: boolean;
}>({
  className: createReadableClass({
    component: 'small-play-controls',
    element: 'container'
  })
})`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  min-height: 48px;
  flex-shrink: 0;

  ${({ _showInOneLine }) => !_showInOneLine && 'flex-direction: column;'}
`;

const MainControls = styled.div.attrs({
  className: createReadableClass({
    component: 'small-play-controls',
    element: 'main-controls'
  })
})`
  display: flex;
  align-items: center;
  width: 100%;
`;

const SecondaryControls = styled.div.attrs<{
  _withMainControls: boolean;
  _showInOneLine: boolean;
}>({
  className: createReadableClass({
    component: 'small-play-controls',
    element: 'secondary-controls'
  })
})`
  right: 16px;
  display: flex;
  gap: 16px;
  width: 100%;
  ${({ _withMainControls, _showInOneLine }) => {
    if (!_showInOneLine) {
      return css`
        & > div:nth-last-child(2) {
          margin-left: auto;
        }
      `;
    }

    return css`
      justify-content: ${_withMainControls ? 'end' : 'start'};
    `;
  }};
`;
